import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, map, of, switchMap, timer } from 'rxjs';

/**
 * Tracks how many preview images are being preloaded in the background so the
 * UI can show a small, non-blocking "Updating preview..." indicator and the
 * checkout capture can wait for the preview to settle.
 */
@Injectable({ providedIn: 'root' })
export class PreviewLoadingService {
  /** Delay before the indicator appears, so quick cached swaps never flash it. */
  private static readonly SHOW_DELAY_MS = 250;

  private readonly pending = new BehaviorSubject<number>(0);

  /** true while at least one preload is pending (rising edge debounced, falling edge immediate). */
  readonly loading$: Observable<boolean> = this.pending.pipe(
    map(count => count > 0),
    distinctUntilChanged(),
    switchMap(isPending =>
      isPending
        ? timer(PreviewLoadingService.SHOW_DELAY_MS).pipe(map(() => true))
        : of(false)
    ),
    distinctUntilChanged()
  );

  /**
   * Registers one pending preload. The returned release function is idempotent,
   * so callers can safely call it from every settle / cancel / destroy path
   * without ever double-decrementing the counter.
   */
  begin(): () => void {
    let released = false;
    this.pending.next(this.pending.value + 1);
    return () => {
      if (released) { return; }
      released = true;
      this.pending.next(Math.max(0, this.pending.value - 1));
    };
  }

  /** Resolves once no preload is pending, or after `timeoutMs`, whichever comes first. */
  whenIdle(timeoutMs: number): Promise<void> {
    if (this.pending.value === 0) { return Promise.resolve(); }
    return new Promise<void>(resolve => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const subscription = this.pending.subscribe(count => {
        if (count === 0) { finish(); }
      });
      const finish = () => {
        if (timeoutId !== undefined) { clearTimeout(timeoutId); }
        // The subscription callback can fire synchronously before `subscription` is assigned.
        queueMicrotask(() => subscription.unsubscribe());
        resolve();
      };
      timeoutId = setTimeout(finish, timeoutMs);
    });
  }
}
